/**
 * Mega Menu Async Image Loader
 * Loads appropriate images (mobile vs desktop) based on viewport size
 * Prevents duplicate image downloads for better performance
 */

(function() {
  'use strict';

  // Configuration
  const DEBUG = false; // Set to true to enable console logging
  const BREAKPOINT = 990; // Mobile vs desktop breakpoint (matches theme breakpoint)
  const RESIZE_DEBOUNCE_MS = 300; // Debounce time for resize events
  const IMAGE_CONTAINERS_SELECTOR = '.js-mega-menu-image';

  // Debug logger
  const log = {
    info: function() {
      if (DEBUG) console.log.apply(console, ['%c[MegaMenu]', 'color:#fff;background:#9c27b0;padding:2px 6px;border-radius:3px'].concat(Array.prototype.slice.call(arguments)));
    },
    warn: function() {
      if (DEBUG) console.warn.apply(console, ['%c[MegaMenu]', 'color:#fff;background:#ff9800;padding:2px 6px;border-radius:3px'].concat(Array.prototype.slice.call(arguments)));
    }
  };

  // Track loaded images to prevent duplicates (per-container tracking)
  const loadedImages = new WeakMap();

  /**
   * Determine if viewport is desktop size
   * @returns {boolean}
   */
  function isDesktop() {
    return window.innerWidth >= BREAKPOINT;
  }

  /**
   * Determine if device has high pixel density (Retina, etc.)
   * @returns {boolean}
   */
  function isHighDPI() {
    return (window.devicePixelRatio || 1) >= 2;
  }

  /**
   * Check if a container already has an image loaded
   * @param {HTMLElement} container
   * @returns {boolean}
   */
  function hasLoadedImage(container) {
    // Check WeakMap first
    if (loadedImages.has(container)) {
      return true;
    }
    // Also check if there's already an img element (for section reloads where WeakMap is cleared)
    return container.querySelector('img') !== null;
  }

  /**
   * Create and insert an image element
   * @param {HTMLElement} container - The container to insert the image into
   * @param {string} imageUrl - The image URL
   * @param {string} altText - The alt text for the image
   * @param {string} containerType - 'desktop' or 'mobile'
   */
  function loadImage(container, imageUrl, altText, containerType) {
    if (!imageUrl || !container) return;

    // Check if already loaded
    if (hasLoadedImage(container)) {
      log.info('Skipping already loaded', containerType, 'image for:', altText);
      return;
    }

    log.info('Loading', containerType, 'image for:', altText, '| URL:', imageUrl);

    // Create image element
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = altText || '';

    // Add appropriate class and loading strategy based on context
    if (containerType === 'desktop') {
      img.className = 'mega-menu-enhanced__image';
      img.loading = 'eager';
    } else {
      img.className = 'mobile-mega-menu__image';
      img.loading = 'lazy';
    }

    // Handle successful load
    img.addEventListener('load', function() {
      container.setAttribute('aria-busy', 'false');
      loadedImages.set(container, true);
      log.info('✓ Successfully loaded', containerType, 'image for:', altText);
    });

    // Handle error
    img.addEventListener('error', function() {
      container.setAttribute('aria-busy', 'false');
      log.warn('✗ Failed to load', containerType, 'image:', imageUrl);
    });

    // Clear container and insert image
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    container.appendChild(img);

    // Mark as loaded immediately to prevent duplicate attempts
    loadedImages.set(container, true);
  }

  /**
   * Load images for menu containers
   * @param {Object} options - Optional settings
   * @param {boolean} options.forceMobile - Force load mobile images regardless of viewport
   * @param {boolean} options.forceAll - Force check all containers (for section reloads)
   */
  function loadImages(options) {
    options = options || {};
    const containers = document.querySelectorAll(IMAGE_CONTAINERS_SELECTOR);

    log.info('loadImages called', 'forceMobile:', options.forceMobile, 'forceAll:', options.forceAll, 'containers found:', containers.length);

    if (!containers.length) {
      log.warn('No image containers found');
      return;
    }

    const desktop = isDesktop();
    const highDPI = isHighDPI();

    log.info('Viewport:', desktop ? 'DESKTOP' : 'MOBILE', '| Width:', window.innerWidth, '| HighDPI:', highDPI);

    let loadedCount = 0;
    let skippedCount = 0;

    // Process each container
    containers.forEach(function(container) {
      const altText = container.getAttribute('data-alt') || 'Menu image';
      const isDesktopContainer = container.classList.contains('mega-menu-enhanced__image-container');
      const isMobileContainer = container.classList.contains('mobile-mega-menu__image-container');

      // Skip if already loaded (unless forceAll is set, which re-checks DOM)
      if (!options.forceAll && hasLoadedImage(container)) {
        skippedCount++;
        return;
      }

      let imageUrl = null;
      let containerType = null;

      // Desktop containers - load on desktop viewport
      if (isDesktopContainer && desktop) {
        const desktop2x = container.getAttribute('data-desktop-image-2x');
        const desktop1x = container.getAttribute('data-desktop-image');
        imageUrl = (highDPI && desktop2x) ? desktop2x : desktop1x;
        containerType = 'desktop';
      }
      // Mobile containers - load on mobile viewport OR when forceMobile is true
      else if (isMobileContainer && (!desktop || options.forceMobile)) {
        const mobile2x = container.getAttribute('data-mobile-image-2x');
        const mobile1x = container.getAttribute('data-mobile-image');
        imageUrl = (highDPI && mobile2x) ? mobile2x : mobile1x;
        containerType = 'mobile';
      }

      if (imageUrl && containerType) {
        loadImage(container, imageUrl, altText, containerType);
        loadedCount++;
      }
    });

    log.info('Result: loaded', loadedCount, '| skipped', skippedCount);
  }

  /**
   * Debounce function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction() {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function() {
        func.apply(context, args);
      }, wait);
    };
  }

  /**
   * Handle resize events with debouncing
   */
  const handleResize = debounce(function() {
    log.info('🔄 Resize detected');
    loadImages();
  }, RESIZE_DEBOUNCE_MS);

  /**
   * Initialize the image loader
   */
  function init() {
    log.info('🚀 Initializing (breakpoint: ' + BREAKPOINT + 'px)');

    // Load on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        loadImages();
      });
    } else {
      // DOM already loaded
      loadImages();
    }

    // Listen for resize
    window.addEventListener('resize', handleResize);
  }

  // Initialize
  init();

  // Expose public API
  window.MegaMenuImageLoader = {
    // Standard call - loads mobile images when drawer opens
    loadImages: function() {
      log.info('📱 Manual trigger (forceMobile)');
      loadImages({ forceMobile: true });
    },
    // Force reload - for section refreshes in customizer
    reload: function() {
      log.info('🔄 Force reload (forceAll + forceMobile)');
      loadImages({ forceMobile: true, forceAll: true });
    }
  };
})();
